import { useCallStore } from '../model'
import styles from './LiveStats.module.css'

/**
 * Что происходит со звонком прямо сейчас.
 *
 * Показывает не итоги, а то, по чему судят на ходу: сколько агент
 * думает перед ответом, слышит ли микрофон, сколько раз его перебили.
 * Итоги придут в отчёте — здесь важно другое.
 */
export function LiveStats() {
  const { status, turns, micLevel, agentLevel } = useCallStore()

  const live = status === 'live' || status === 'connecting'

  const replies = turns.filter((t) => t.role === 'agent' && t.replyMs > 0)
  const lastReply = replies[replies.length - 1]?.replyMs ?? 0
  const avgReply = replies.length
    ? Math.round(replies.reduce((sum, t) => sum + t.replyMs, 0) / replies.length)
    : 0

  const interrupts = turns.filter((t) => t.interrupted).length
  const humanTurns = turns.filter((t) => t.role === 'human').length

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.title}>Ход разговора</span>
        {live && (
          <span className={styles.live}>
            <span className={styles.liveDot} />
            запись
          </span>
        )}
      </div>

      <div className={styles.meterRow}>
        <div className={styles.meterHead}>
          <span className={styles.label}>Ваш микрофон</span>
          <span className={styles.value}>{level(micLevel)}</span>
        </div>
        <div className={styles.meter}>
          <div
            className={`${styles.meterFill} ${styles.meterHuman}`}
            style={{ width: `${Math.min(micLevel * 240, 100)}%` }}
          />
        </div>
      </div>

      <div className={styles.meterRow}>
        <div className={styles.meterHead}>
          <span className={styles.label}>Голос агента</span>
          <span className={styles.value}>{level(agentLevel)}</span>
        </div>
        <div className={styles.meter}>
          <div
            className={styles.meterFill}
            style={{ width: `${Math.min(agentLevel * 160, 100)}%` }}
          />
        </div>
      </div>

      <div className={styles.rows}>
        <div className={styles.row}>
          <span className={styles.label}>Последний ответ</span>
          <span className={`${styles.value} ${replyClass(lastReply)}`}>
            {lastReply ? `${(lastReply / 1000).toFixed(2)} с` : '—'}
          </span>
        </div>

        <div className={styles.row}>
          <span className={styles.label}>В среднем</span>
          <span className={`${styles.value} ${replyClass(avgReply)}`}>
            {avgReply ? `${(avgReply / 1000).toFixed(2)} с` : '—'}
          </span>
        </div>

        <div className={styles.row}>
          <span className={styles.label}>Ваших реплик</span>
          <span className={styles.value}>{humanTurns}</span>
        </div>

        <div className={styles.row}>
          <span className={styles.label}>Перебиваний</span>
          <span className={styles.value}>{interrupts}</span>
        </div>
      </div>

      <p className={styles.tip}>
        {tip(status, humanTurns, interrupts)}
      </p>
    </div>
  )
}

/**
 * Подсказка по ходу дела.
 *
 * Меняется по обстановке: пока не сказано ни слова — что делать, после
 * первых реплик — что ещё стоит попробовать. Постоянный текст в этом
 * месте перестают читать на втором звонке.
 */
function tip(status: string, humanTurns: number, interrupts: number): string {
  if (status === 'connecting') return 'Разрешите доступ к микрофону, когда браузер спросит.'
  if (humanTurns === 0) return 'Агент начнёт первым. Отвечайте как обычный человек.'
  if (interrupts === 0) return 'Попробуйте перебить его на полуслове — он должен уступить.'
  return 'Возразите или откажите: интереснее слушать, как он отрабатывает отказ.'
}

/** Задержка ответа. Пороги те же, что подсвечены в стенограмме. */
function replyClass(ms: number): string {
  if (!ms) return ''
  if (ms > 2500) return styles.valueBad ?? ''
  if (ms > 1500) return styles.valueWarn ?? ''
  return styles.valueGood ?? ''
}

/** Громкость в децибелах: понятнее, чем доля от единицы. */
function level(value: number): string {
  if (value <= 0.001) return 'тихо'
  return `${Math.round(20 * Math.log10(value))} дБ`
}
